import React from 'react';
import { Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

/**
 * LeadSignalGuide — small (?) help icon explaining all the visual
 * indicators on the Leads page (heat dots, badges, status colors,
 * etc.). Lives in the toolbar so it's discoverable without taking
 * up real estate on every row.
 */
export function LeadSignalGuide() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "group inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[11px] font-bold transition-all",
            "bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 text-white",
            "shadow-sm shadow-violet-200 hover:shadow-md hover:scale-[1.03]",
            "ring-1 ring-violet-300/40"
          )}
          title="What do these signals mean?"
          data-testid="leads-signal-guide-btn"
        >
          <Sparkles size={11} className="text-violet-200 group-hover:text-white transition-colors" />
          <span>Kai Intelligence</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="w-[360px] p-0 overflow-hidden border-slate-200 shadow-2xl"
      >
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-3 text-white">
          <div className="flex items-center gap-1.5">
            <Sparkles size={13} className="text-violet-300" />
            <div className="text-[10px] uppercase tracking-wider font-bold text-violet-200">Legend</div>
          </div>
          <div className="mt-0.5 text-[14px] font-bold">Lead Page Signals</div>
        </div>

        <div className="p-4 space-y-3.5 text-[11.5px] text-slate-700">
          {/* Heat dot */}
          <section>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Activity Heat</div>
            <div className="space-y-1">
              <Row dot="emerald" label="Active" desc="Updated in the last 3 days" />
              <Row dot="amber" label="Cooling" desc="3 – 14 days since last update" />
              <Row dot="rose" label="Stale" desc="No activity in 14+ days — needs attention" />
              <Row dot="slate" label="Unknown" desc="No activity timestamp recorded" />
            </div>
          </section>

          {/* New vs Returning */}
          <section>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Patient Type</div>
            <div className="space-y-1">
              <Badge label="New" color="emerald" desc="No Juvonno record — first-time lead" />
              <Badge label="Returning" color="amber" desc="Has a Juvonno ID or a prior won lead" />
            </div>
          </section>

          {/* Kai AI */}
          <section>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Kai Intelligence ✨</div>
            <p className="text-[11px] leading-snug text-slate-600">
              Click the violet <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 text-white align-middle mx-0.5"><Sparkles size={7} /></span> icon next to a lead&apos;s name for a one-sentence Kai AI summary, the last channel message, the most recent call, and a <span className="font-semibold text-violet-600">Suggested Next Action</span> (Book Apt, Send another SMS, Email to book, Assign FD Team, or Update Lead Status) with a one-click execute button. Cached 24h.
            </p>
          </section>

          {/* Days pill */}
          <section>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">&ldquo;Days Since Update&rdquo; Pill</div>
            <div className="space-y-1">
              <Pill color="emerald" label="0 – 2d" desc="Fresh" />
              <Pill color="amber" label="3 – 7d" desc="Cooling — follow up soon" />
              <Pill color="rose" label="8d+" desc="Stale — urgent follow-up" />
            </div>
          </section>

          {/* Status badges */}
          <section>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Lead Status Pills</div>
            <div className="grid grid-cols-2 gap-1">
              <Status color="blue" label="Open" />
              <Status color="emerald" label="Won" />
              <Status color="slate" label="Lost" />
              <Status color="rose" label="Cancelled" />
            </div>
          </section>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const dotMap = {
  emerald: 'bg-emerald-500 shadow-emerald-200',
  amber: 'bg-amber-500 shadow-amber-200',
  rose: 'bg-rose-500 shadow-rose-200',
  slate: 'bg-slate-300 shadow-slate-200',
};
const Row = ({ dot, label, desc }) => (
  <div className="flex items-center gap-2">
    <span className={`w-2 h-2 rounded-full shadow-sm ${dotMap[dot]}`} />
    <span className="font-semibold text-slate-800 w-[60px]">{label}</span>
    <span className="text-slate-500 text-[11px]">{desc}</span>
  </div>
);

const badgeColorMap = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
};
const Badge = ({ label, color, desc }) => (
  <div className="flex items-center gap-2">
    <span className={`px-1.5 py-px text-[9px] font-bold rounded-full border ${badgeColorMap[color]}`}>{label}</span>
    <span className="text-slate-500 text-[11px]">{desc}</span>
  </div>
);

const pillColorMap = {
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  rose: 'bg-rose-50 text-rose-600',
};
const Pill = ({ color, label, desc }) => (
  <div className="flex items-center gap-2">
    <span className={`text-[9px] font-bold px-1.5 py-px rounded ${pillColorMap[color]} w-[60px] text-center`}>{label}</span>
    <span className="text-slate-500 text-[11px]">{desc}</span>
  </div>
);

const statusColorMap = {
  blue: 'bg-blue-100 text-blue-700 border-blue-300',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  slate: 'bg-slate-100 text-slate-500 border-slate-300',
  rose: 'bg-rose-100 text-rose-700 border-rose-300',
};
const Status = ({ color, label }) => (
  <span className={`text-[10px] font-bold border rounded-full px-2 py-0.5 text-center ${statusColorMap[color]}`}>{label}</span>
);

export default LeadSignalGuide;
